import React, { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Icon from '@/components/ui/Icon'
import { Badge } from '@/components/ui/badge'
import { getBetaUsers, addBetaUser, updateBetaUser } from '@/services/adminApi'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const AdminBetaManagement = () => {
  const [betaUsers, setBetaUsers] = useState([])
  const [betaRequests, setBetaRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState('users') // 'users' or 'requests'
  
  // Add user modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    notes: ''
  })
  const [addingUser, setAddingUser] = useState(false)

  useEffect(() => {
    // Add a small delay to ensure the proxy is fully ready
    const timer = setTimeout(() => {
      loadData();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Use the new admin API service
      const usersData = await getBetaUsers()
      const allBetaUsers = usersData.beta_users || []

      // Separate by status for display
      setBetaUsers(allBetaUsers.filter(u => u.status === 'approved'))
      setBetaRequests(allBetaUsers.filter(u => u.status === 'pending'))

    } catch (error) {
      console.error('Error loading beta data:', error)
      setError('Failed to load beta management data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddUser = async () => {
    if (!newUser.email) return

    try {
      setAddingUser(true)
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch('/api/admin/beta-users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      })

      if (!response.ok) throw new Error('Failed to add beta user')

      const result = await response.json()
      if (result.success) {
        // Reload data to get updated lists
        loadData()
        setNewUser({ email: '', firstName: '', lastName: '', notes: '' })
        setShowAddUserModal(false)
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      console.error('Error adding beta user:', error)
      setError(error.message)
    } finally {
      setAddingUser(false)
    }
  }

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(`/api/admin/beta-users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      })

      if (!response.ok) throw new Error('Failed to update user status')

      const result = await response.json()
      if (result.success) {
        // Reload data to get updated lists
        loadData()
      }
    } catch (error) {
      console.error('Error updating user status:', error)
      setError(error.message)
    }
  }

  const handleUpdateRequestStatus = async (requestId, status, adminNotes = '') => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(`/api/admin/beta-users/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, notes: adminNotes })
      })

      if (!response.ok) throw new Error('Failed to update request status')

      const result = await response.json()
      if (result.success) {
        // Reload data to get updated lists
        loadData()
      }
    } catch (error) {
      console.error('Error updating request status:', error)
      setError(error.message)
    }
  }

  const getStatusBadge = (status) => {
    const variants = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      revoked: 'bg-gray-100 text-gray-800'
    }
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    )
  }

  const filteredUsers = betaUsers.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const filteredRequests = betaRequests.filter(request =>
    request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (request.full_name && request.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (request.city && request.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (request.state && request.state.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icon name="loader" size="lg" className="animate-spin text-brand-600" />
        <span className="ml-2 text-slate-600">Loading beta management data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button
          onClick={() => setShowAddUserModal(true)}
          className="bg-brand-600 hover:bg-brand-700"
        >
          <Icon name="plus" size="sm" className="mr-2" />
          Add Beta User
        </Button>
      </div>

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 border rounded-lg bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 mb-1">Approved Users</p>
              <p className="text-2xl font-bold text-green-800">
                {betaUsers.filter(u => u.status === 'approved').length}
              </p>
            </div>
            <Icon name="users" className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="p-4 border rounded-lg bg-yellow-50 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600 mb-1">Pending Requests</p>
              <p className="text-2xl font-bold text-yellow-800">
                {betaRequests.filter(r => r.status === 'pending').length}
              </p>
            </div>
            <Icon name="clock" className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 mb-1">Total Users</p>
              <p className="text-2xl font-bold text-blue-800">{betaUsers.length}</p>
            </div>
            <Icon name="user-check" className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="p-4 border rounded-lg bg-purple-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-brand-600 mb-1">Total Requests</p>
              <p className="text-2xl font-bold text-brand-800">{betaRequests.length}</p>
            </div>
            <Icon name="mail" className="w-8 h-8 text-brand-600" />
          </div>
        </div>

        <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-600 mb-1">CA Requests</p>
              <p className="text-2xl font-bold text-amber-800">
                {betaRequests.filter(req => req.state === 'CA').length}
              </p>
            </div>
            <Icon name="alert-triangle" className="w-8 h-8 text-amber-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-purple-500 text-brand-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Beta Users ({betaUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'requests'
                ? 'border-purple-500 text-brand-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Access Requests ({betaRequests.length})
          </button>
        </nav>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <Input
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
      </div>

      {/* Beta Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invited By</TableHead>
                <TableHead>Approved Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell>{user.invited_by || 'N/A'}</TableCell>
                  <TableCell>
                    {user.approved_at ? new Date(user.approved_at).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {user.status === 'approved' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateUserStatus(user.id, 'revoked')}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleUpdateUserStatus(user.id, 'approved')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Approve
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Beta Requests Tab */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className="font-medium">
                    {request.full_name || `${request.first_name || ''} ${request.last_name || ''}`.trim() || 'N/A'}
                  </TableCell>
                  <TableCell>{request.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{request.city || 'N/A'}</span>
                      <span className="text-sm text-gray-500">{request.state || 'N/A'}</span>
                      {request.state === 'CA' && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 mt-1">
                          CA - Not Supported
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {request.reason || 'No reason provided'}
                  </TableCell>
                  <TableCell>{getStatusBadge(request.status)}</TableCell>
                  <TableCell>
                    {new Date(request.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {request.status === 'pending' && (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateRequestStatus(request.id, 'approved')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateRequestStatus(request.id, 'rejected')}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Beta User</DialogTitle>
            <DialogDescription>
              Add a new user to the approved beta users list.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={newUser.notes}
                onChange={(e) => setNewUser(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddUserModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddUser}
              disabled={!newUser.email || addingUser}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {addingUser ? (
                <>
                  <Icon name="loader" size="sm" className="mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AdminBetaManagement
